// src/login.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMoon, FaSun, FaSpinner } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

export default function Login() {
  const navigate = useNavigate();

  const [dark, setDark] = useState(true);
  const [phone, setPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');

  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);

  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [otpError, setOtpError] = useState('');

  useEffect(() => {
    let timer;
    if (count > 0) {
      timer = setTimeout(() => setCount((c) => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [count]);

  const colors = useMemo(() => {
    if (dark) {
      return {
        bg: '#070B14',
        bg2: '#08101C',
        bg3: '#0A1224',
        card: 'rgba(21,29,51,0.96)',
        card2: 'rgba(14,20,37,0.96)',
        text: '#FFFFFF',
        sub: '#B7C0D1',
        muted: '#8FA0BA',
        primary1: '#2563eb',
        primary2: '#7c3aed',
        border: 'rgba(255,255,255,0.08)',
        inputBg: '#10182A',
        overlay: 'rgba(0,0,0,0.55)',
        danger: '#ef4444',
      };
    }

    return {
      bg: '#F8FAFF',
      bg2: '#F2F6FF',
      bg3: '#EDF2FF',
      card: 'rgba(255,255,255,0.96)',
      card2: 'rgba(245,248,255,0.96)',
      text: '#0F172A',
      sub: '#475569',
      muted: '#64748B',
      primary1: '#2563eb',
      primary2: '#7c3aed',
      border: 'rgba(15,23,42,0.10)',
      inputBg: '#FFFFFF',
      overlay: 'rgba(15,23,42,0.28)',
      danger: '#ef4444',
    };
  }, [dark]);

  const validatePhone = (value) => {
    const normalized = value.replace(/[^\d]/g, '').slice(0, 11);
    setPhone(normalized);

    if (!normalized) {
      setPhoneError('');
      return;
    }

    if (!/^09\d{9}$/.test(normalized)) {
      setPhoneError('فرمت شماره موبایل صحیح نیست');
    } else {
      setPhoneError('');
    }
  };

  const validateOtp = (value) => {
    const normalized = value.replace(/[^\d]/g, '').slice(0, 6);
    setOtp(normalized);

    if (!normalized) {
      setOtpError('');
      return;
    }

    if (!/^\d{4,6}$/.test(normalized)) {
      setOtpError('کد باید عددی و بین ۴ تا ۶ رقم باشد');
    } else {
      setOtpError('');
    }
  };

  const sendOtp = async () => {
    if (!/^09\d{9}$/.test(phone)) {
      setPhoneError('فرمت شماره موبایل صحیح نیست');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const res = await fetch(`${API_URL}/api/auth/send-otp/${phone}`, {
        method: 'POST',
      });

      if (res.ok) {
        setOtpSent(true);
        setCount(120);
        return;
      }

      let msg = 'خطا در ارسال کد';
      try {
        const data = await res.json();
        msg = data?.Message || data?.message || msg;
      } catch {}

      setError(msg);
    } catch {
      setError('ارسال کد ناموفق بود');
    } finally {
      setLoading(false);
    }
  };

  const goAfterLogin = () => {
    const rawRedirect = localStorage.getItem('redirectAfterLogin');

    if (!rawRedirect) {
      navigate('/dashboard', { replace: true });
      return;
    }

    try {
      const redirect = JSON.parse(rawRedirect);
      localStorage.removeItem('redirectAfterLogin');

      const to = redirect?.to || '/dashboard';
      const state = redirect?.state || undefined;

      navigate(to, { replace: true, state });
    } catch {
      localStorage.removeItem('redirectAfterLogin');
      navigate('/dashboard', { replace: true });
    }
  };

  const verifyOtp = async () => {
    if (!/^\d{4,6}$/.test(otp)) {
      setOtpError('کد باید عددی و بین ۴ تا ۶ رقم باشد');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const res = await fetch(`${API_URL}/api/auth/login/otp/web`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: phone, otpCode: otp }),
      });

      const data = await res.json();

      if (res.ok && data?.jwt) {
        localStorage.setItem('token', data.jwt);
        goAfterLogin();
        return;
      }

      setError(data?.Message || data?.message || 'کد تأیید نادرست است');
    } catch {
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  const changePhone = () => {
    if (loading) return;

    setOtpSent(false);
    setOtp('');
    setOtpError('');
    setError('');
    setCount(0);
  };

  const TermsLine = () => (
    <p className="mt-4 text-xs leading-6 text-center" style={{ color: colors.sub }}>
      با ورود به اپلیکیشن{' '}
      <button
        type="button"
        onClick={() => navigate('/terms')}
        className="font-bold"
        style={{ color: colors.primary1 }}
      >
        شرایط و مقررات
      </button>
      {' '}
      {' '}مورد قبول می‌باشد.
    </p>
  );

  return (
    <div dir="rtl" className={dark ? 'dark' : ''}>
      <div
        className="min-h-screen font-vazir relative overflow-hidden flex items-center justify-center px-4 py-8"
        style={{
          background: `linear-gradient(135deg, ${colors.bg}, ${colors.bg2}, ${colors.bg3})`,
          color: colors.text,
        }}
      >
        <div className="absolute top-20 right-[-80px] w-72 h-72 rounded-full bg-purple-500/20 blur-3xl" />
        <div className="absolute bottom-20 left-[-80px] w-72 h-72 rounded-full bg-blue-500/20 blur-3xl" />

        {loading && (
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center"
            style={{ backgroundColor: colors.overlay }}
          >
            <div
              className="min-w-[130px] min-h-[130px] rounded-[24px] border flex flex-col items-center justify-center shadow-2xl"
              style={{
                background: `linear-gradient(135deg, ${colors.card}, ${colors.card2})`,
                borderColor: colors.border,
              }}
            >
              <FaSpinner className="animate-spin text-3xl" style={{ color: colors.primary1 }} />
              <div className="mt-4 text-sm font-bold">در حال پردازش...</div>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setDark((v) => !v)}
          className="absolute top-5 right-5 z-30 w-12 h-12 rounded-2xl border flex items-center justify-center shadow-lg"
          style={{
            background: dark ? 'linear-gradient(135deg,#111827,#1F2937)' : 'linear-gradient(135deg,#FFFFFF,#F1F5F9)',
            borderColor: colors.border,
            color: colors.text,
          }}
        >
          {dark ? <FaSun /> : <FaMoon />}
        </button>

        <div
          className="relative z-10 w-full max-w-[430px] rounded-[28px] border p-6 md:p-7 shadow-2xl backdrop-blur-xl"
          style={{
            background: `linear-gradient(135deg, ${colors.card}, ${colors.card2})`,
            borderColor: colors.border,
          }}
        >
          <button
            type="button"
            onClick={() => navigate('/')}
            className="block mx-auto"
          >
            <img
              src="/logo.png"
              alt="Mogym"
              className="w-24 h-24 mx-auto mb-5 rounded-3xl"
            />
          </button>

          <h1 className="text-2xl md:text-3xl font-black text-center">
            {!otpSent ? 'ورود به موجیم' : 'تایید شماره موبایل'}
          </h1>

          <p className="mt-3 mb-6 text-sm leading-7 text-center" style={{ color: colors.sub }}>
            {!otpSent
              ? 'شماره موبایل خودت را وارد کن تا کد یکبارمصرف برایت ارسال شود.'
              : `کد ارسال‌شده به ${phone} را وارد کن.`}
          </p>

          {!otpSent ? (
            <>
              <label className="block mb-2 text-sm font-bold text-right">
                شماره موبایل
              </label>

              <input
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => validatePhone(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') sendOtp();
                }}
                placeholder="09xxxxxxxxx"
                className="w-full rounded-2xl border px-4 py-4 outline-none text-right transition"
                style={{
                  backgroundColor: colors.inputBg,
                  borderColor: phoneError ? colors.danger : colors.border,
                  color: colors.text,
                }}
              />

              {phoneError && (
                <p className="mt-2 mb-3 text-xs text-right" style={{ color: colors.danger }}>
                  {phoneError}
                </p>
              )}

              <button
                type="button"
                onClick={sendOtp}
                disabled={!phone || !!phoneError || loading}
                className="mt-4 w-full h-14 rounded-2xl text-white font-black transition hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary1}, ${colors.primary2})`,
                }}
              >
                ارسال کد تایید
              </button>

              <TermsLine />
            </>
          ) : (
            <>
              <div
                className="rounded-2xl border p-4 mb-5 text-center"
                style={{
                  backgroundColor: colors.inputBg,
                  borderColor: colors.border,
                }}
              >
                <div className="text-xs" style={{ color: colors.sub }}>
                  کد تایید برای این شماره ارسال شده است
                </div>
                <div className="mt-2 font-black">{phone}</div>
              </div>

              <label className="block mb-2 text-sm font-bold text-right">
                کد تایید
              </label>

              <input
                type="tel"
                inputMode="numeric"
                value={otp}
                onChange={(e) => validateOtp(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') verifyOtp();
                }}
                placeholder="— — — —"
                maxLength={6}
                className="w-full rounded-2xl border px-4 py-4 outline-none text-center text-xl tracking-[10px] transition"
                style={{
                  backgroundColor: colors.inputBg,
                  borderColor: otpError ? colors.danger : colors.border,
                  color: colors.text,
                }}
              />

              {otpError && (
                <p className="mt-2 mb-3 text-xs text-right" style={{ color: colors.danger }}>
                  {otpError}
                </p>
              )}

              <button
                type="button"
                onClick={verifyOtp}
                disabled={!otp || !!otpError || loading}
                className="mt-4 w-full h-14 rounded-2xl text-white font-black transition hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary1}, ${colors.primary2})`,
                }}
              >
                تایید و ورود
              </button>

              <TermsLine />

              <div className="mt-5 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => count === 0 && !loading && sendOtp()}
                  disabled={count !== 0 || loading}
                  className="text-sm font-bold disabled:opacity-60"
                  style={{ color: count === 0 ? colors.primary1 : colors.sub }}
                >
                  ارسال مجدد {count > 0 ? `(${count})` : ''}
                </button>

                <button
                  type="button"
                  onClick={changePhone}
                  disabled={loading}
                  className="text-sm disabled:opacity-60"
                  style={{ color: colors.sub }}
                >
                  تغییر شماره
                </button>
              </div>
            </>
          )}

          {error && (
            <div
              className="mt-5 rounded-2xl border p-3 text-center text-sm font-bold"
              style={{
                borderColor: 'rgba(239,68,68,0.25)',
                backgroundColor: dark ? 'rgba(127,29,29,0.20)' : 'rgba(254,242,242,0.96)',
                color: colors.danger,
              }}
            >
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}